import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { FormHandlerService } from 'app/shared/forms/index';
import { ApolloMutationService } from 'app/shared/apollo/index';
import { ButtonsAsync } from 'app/shared/forms/buttons-async';

@Component({
  selector: 'app-contracts-row',
  templateUrl: './contracts-row.component.html',
  providers: [FormHandlerService],
})
export class ContractsRowComponent implements OnDestroy, OnInit {
  @Input() offerInfo;
  @Input() dataObjectFromParent;
  @Output() spliceObjectNotYetCreated = new EventEmitter();
  @Output() deletedObject = new EventEmitter();
  @Output() createdObject = new EventEmitter();

  buttons: ButtonsAsync;
  buttonObject = ['delete'];
  dataModel = 'offerContractor';
  dataModelCapitalized = 'OfferContractor';
  keyUp = new Subject<string>();
  inputSubscription: Subscription;
  formFields = {
    model: this.dataModelCapitalized,
    attributes: {},
  };

  componentMainForm: FormGroup;

  constructor(
    private formHandler: FormHandlerService,
    private mutationService: ApolloMutationService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit() {
    this.buttons = this.formHandler.getButtonValuesAsync(this.buttonObject);
    this.initForm();
    this.subscribeToInput();
  }

  ngOnDestroy() {
    this.inputSubscription && this.inputSubscription.unsubscribe();
  }

  subscribeToInput() {
    this.inputSubscription = this.keyUp
      .pipe(
        map(event => event['target']['value']),
        debounceTime(2500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.handleInput();
      });
  }

  initForm() {
    this.formFields.attributes = { ...this.dataObjectFromParent };
    this.componentMainForm = this.formHandler.groupedFormSimple(
      this.formFields
    );
    this.formHandler
      .groupSetLabelsRules(this.formFields, this.componentMainForm)
      .then();
  }

  handleInput() {
    const id = this.componentMainForm['controls']['id']['value'];
    const buisness = this.componentMainForm['controls']['buisness']['value'];

    if (buisness && buisness !== '' && typeof buisness !== 'undefined') {
      if (id === null) {
        this.actionCreate();
      } else {
        this.actionUpdate();
      }
    }
  }

  actionCreate() {
    const crudType = 'create';
    const dataToMutation = this.formHandler.getCleanedMutationVariable(
      this.componentMainForm
    );
    dataToMutation['offerId'] = Number(this.offerInfo['id']);
    delete dataToMutation['id'];

    this.formHandler.lockButtonsAsync(this.buttons);

    if (!this.formHandler.formValid([this.componentMainForm])) {
      this.formHandler.unlockButtonsAsync(this.buttons);
    } else {
      const returnKeys = Object.keys(this.componentMainForm.value);
      const executeMutationSub = this.mutationService
        .constructQueryAndExecuteMutation(
          this.dataModel,
          crudType,
          false,
          dataToMutation,
          returnKeys
        )
        .subscribe(
          executedData => {
            this.formHandler.showServerErrorsOnAttributes(executedData, [
              {
                form: this.componentMainForm,
                argument: crudType + this.dataModelCapitalized,
              },
            ]);

            if (executedData.mutationSucceededAllArguments) {
              this.handleCreatedObject(executedData);
            }

            this.formHandler.unlockButtonsAsync(this.buttons);

            this.mutationService.displayMutationStatus(executedData);

            executeMutationSub.unsubscribe();
          },
          err => {
            this.formHandler.unlockButtonsAsync(this.buttons);
            console.log(err);
          }
        );
    }
  }

  handleCreatedObject(executedData) {
    this.componentMainForm['controls']['id'].setValue(executedData['id']);
    const createdObject = executedData;
    delete createdObject['mutationDetails'];
    delete createdObject['__typename'];

    this.createdObject.emit(createdObject);
  }

  actionUpdate() {
    const crudType = 'update';
    const dataToMutation = this.formHandler.getCleanedMutationVariable(
      this.componentMainForm
    );
    this.formHandler.lockButtonsAsync(this.buttons);

    if (!this.formHandler.formValid([this.componentMainForm])) {
      this.formHandler.unlockButtonsAsync(this.buttons);
    } else {
      const executeMutationSub = this.mutationService
        .constructQueryAndExecuteMutation(
          this.dataModel,
          crudType,
          false,
          dataToMutation
        )
        .subscribe(
          executedData => {
            this.formHandler.showServerErrorsOnAttributes(executedData, [
              {
                form: this.componentMainForm,
                argument: crudType + this.dataModelCapitalized,
              },
            ]);

            this.mutationService.displayMutationStatus(executedData);

            this.formHandler.unlockButtonsAsync(this.buttons);

            executeMutationSub.unsubscribe();
          },
          err => {
            this.formHandler.unlockButtonsAsync(this.buttons);
            console.log(err);
          }
        );
    }
  }

  handleDelete() {
    const id = this.componentMainForm['controls']['id']['value'];

    if (id === null) {
      this.spliceObjectNotYetCreated.emit();
    } else {
      this.actionDelete();
    }
  }

  actionDelete() {
    const crudType = 'delete';
    const dataToMutation = { id: Number(this.dataObjectFromParent.id) };

    this.confirmationService.confirm({
      message: 'Är du säker på att du vill ta bort underentreprenören?',
      header: 'Bekräfta val',
      icon: 'fa fa-trash',
      accept: () => {
        this.formHandler.lockButtonsAsync(this.buttons, true);

        const executeMutationSub = this.mutationService
          .constructQueryAndExecuteMutation(
            this.dataModel,
            crudType,
            false,
            dataToMutation
          )
          .subscribe(
            data => {
              this.formHandler.showServerErrorsOnAttributes(data, [
                {
                  form: this.componentMainForm,
                  argument: crudType + this.dataModelCapitalized,
                },
              ]);

              if (data.mutationSucceededAllArguments) {
                this.deletedObject.emit(this.dataObjectFromParent['id']);
              }

              this.mutationService.displayMutationStatus(data);

              this.formHandler.unlockButtonsAsync(this.buttons, true);

              executeMutationSub.unsubscribe();
            },
            err => {
              this.formHandler.unlockButtonsAsync(this.buttons, true);
              console.log(err);
            }
          );
      },
      reject: () => {},
    });
  }
}
